"use client";

import { motion } from "framer-motion";
import backgroundOverlay from "@/app/(public)/shared/assets/background-overlay2.png";
import { Footer } from "@/app/(public)/shared/components/Footer";
import { Navbar } from "@/app/(public)/shared/components/Navbar";
import { Button } from "@/app/(public)/shared/ui/Button";
import "@/app/(public)/shared/tokens/animations.css";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/spacing.css";
import "@/app/(public)/shared/tokens/typography.css";
import { HangingTeamCard } from "./_components/HangingTeamCard";
import { SimpleTeamCard } from "./_components/SimpleTeamCard";
import { ROWS_PER_PAGE } from "./_logic/constants";
import { teamMembers, type TeamMember } from "./_logic/team-members";

/**
 * Public "/team" page. Renders the Nowadays team as animated cards — a simple
 * grid on mobile/tablet and tilted "hanging" cards on desktop — plus the page
 * header and call-to-action buttons.
 * @returns The rendered team page.
 */
export default function Team() {
  return (
    <div className="overflow-x-clip bg-landing-primary relative">
      {/* Background image with opacity */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: `url(${backgroundOverlay.src})`,
          backgroundSize: "100% auto",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
          opacity: 0.3,
        }}
      />

      {/* Content container with higher z-index */}
      <div className="relative z-2">
        <Navbar />
        <main className="container-page">
          <section className="container-top-form">
            <div>
              {/* Header */}
              <motion.div
                className="text-left lg:text-center max-w-4xl lg:mx-auto"
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              >
                <motion.h1
                  className="text-h1 text-landing-primary mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  Meet the people
                  <br />
                  behind Nowadays
                </motion.h1>
                <motion.div
                  className="flex flex-col lg:items-center lg:justify-center gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <p className="text-body-lg text-landing-secondary max-w-2xl">
                    We're a passionate team of builders, designers, and event enthusiasts working to make corporate
                    event planning effortless and delightful. Meet the minds behind the AI-powered platform that's
                    revolutionizing how people come together.
                  </p>
                  <motion.div
                    className="flex flex-col sm:flex-row justify-start lg:justify-center items-start lg:items-center gap-2"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Button
                      variant="primary"
                      color="primary"
                      size="lg"
                      onClick={() => (window.location.href = "/careers")}
                      className="w-full sm:w-auto"
                    >
                      Join us
                    </Button>
                    <Button
                      variant="outline"
                      color="neutral"
                      size="lg"
                      onClick={() => (window.location.href = "/company")}
                      className="w-full sm:w-auto"
                    >
                      Learn more
                    </Button>
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* Team Cards Grid */}
              <motion.div
                className="w-full max-w-5xl mx-auto"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              >
                {/* Mobile/Tablet Simple Grid (md and below) */}
                <div className="block lg:hidden">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {teamMembers.map((member, index) => (
                      <SimpleTeamCard key={member.id} member={member} index={index} />
                    ))}
                  </div>
                </div>

                {/* Desktop Hanging Cards (lg and up) */}
                <div className="hidden lg:block">
                  {(() => {
                    const rows: TeamMember[][] = [];
                    for (let i = 0; i < teamMembers.length; i += ROWS_PER_PAGE) {
                      rows.push(teamMembers.slice(i, i + ROWS_PER_PAGE));
                    }

                    return rows.map((rowMembers, rowIndex) => {
                      const rowCardCount = rowMembers.length;
                      const startIndex = rowIndex * ROWS_PER_PAGE;
                      const containerClass =
                        rowCardCount === 1
                          ? "grid grid-cols-1 gap-1 -mb-4 w-1/3"
                          : rowCardCount === 2
                            ? "grid grid-cols-2 gap-1 -mb-4 w-2/3"
                            : "grid grid-cols-3 gap-1 -mb-4 w-full";

                      return (
                        <motion.div
                          key={rowIndex}
                          className="relative"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, delay: 0.8 + rowIndex * 0.2, ease: [0.16, 1, 0.3, 1] }}
                        >
                          {/* Elegant hanging curve */}
                          <div className="relative w-full h-40 mb-4">
                            <svg
                              className="absolute top-28 left-0 w-full h-40"
                              viewBox="0 0 400 160"
                              preserveAspectRatio="none"
                            >
                              {/* Refined hanging wire with gradient effect */}
                              <defs>
                                <linearGradient id={`wireGradient${rowIndex + 1}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset="0%" stopColor="#9ca3af" stopOpacity="0" />
                                  <stop offset="50%" stopColor="#9ca3af" stopOpacity="0.8" />
                                  <stop offset="100%" stopColor="#9ca3af" stopOpacity="0" />
                                </linearGradient>
                              </defs>
                              <path
                                d="M 0 10 Q 200 180 400 10"
                                stroke={`url(#wireGradient${rowIndex + 1})`}
                                strokeWidth="2"
                                fill="none"
                              />
                            </svg>
                          </div>

                          <div className="flex justify-center">
                            <div className={containerClass}>
                              {rowMembers.map((member, positionInRow) => (
                                <HangingTeamCard
                                  key={member.id}
                                  member={member}
                                  index={startIndex + positionInRow}
                                  positionInRow={positionInRow}
                                  rowCardCount={rowCardCount}
                                  centerRotationDirection={rowIndex % 2 === 0 ? "left" : "right"}
                                />
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      );
                    });
                  })()}
                </div>
              </motion.div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </div>
  );
}
